import { supabase } from '../config/supabase';
import { Session } from '@supabase/supabase-js';
import { Linking } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

interface AuthUser {
  id: string;
  display_name: string;
  email?: string;
  cellphone?: string;
  created_at: string;
}

class AuthService {
  private static instance: AuthService;

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
      // 设置深度链接监听
      AuthService.instance.setupDeepLinkListener();
      console.log('AuthService initialized with deep link listener');
    }
    return AuthService.instance;
  }

  private setupDeepLinkListener() {
    // 监听深度链接事件
    const subscription = Linking.addEventListener('url', this.handleDeepLink);

    // 检查应用启动时是否有初始URL
    Linking.getInitialURL().then((url) => {
      if (url) {
        console.log('Initial URL found:', url);
        this.handleDeepLink({ url });
      }
    }).catch((error) => {
      console.error('Error getting initial URL:', error);
    });

    return subscription;
  }

  // 离线昵称检查（使用本地存储）
  private checkNicknameOffline(nickname: string): any[] {
    // 在离线模式下，我们无法确实检查昵称是否已被使用
    // 返回空数组表示昵称可用，但在提示中告知用户网络状态
    console.log('Using offline nickname check for:', nickname);
    return [];
  }

  // 离线保存用户信息
  private async saveUserOffline(userId: string, nickname: string, password: string, createdAt: string): Promise<void> {
    try {
      // 保存到本地存储，待网络恢复后同步
      const offlineUser = {
        id: userId,
        display_name: nickname,
        password_hash: `simple_${password}`,
        created_at: createdAt,
        sync_pending: true,
      };
      
      await AsyncStorage.setItem(`offline_user_${userId}`, JSON.stringify(offlineUser));
      console.log('用户信息已保存到本地，待网络恢复后同步');
    } catch (error) {
      console.error('保存离线用户信息失败:', error);
    }
  }

  // 加载离线用户信息
  private async loadUserOffline(nickname: string, password: string): Promise<AuthUser | null> {
    try {
      const keys = await AsyncStorage.getAllKeys();
      const offlineUserKeys = keys.filter(key => key.startsWith('offline_user_'));
      
      for (const key of offlineUserKeys) {
        const userJson = await AsyncStorage.getItem(key);
        if (userJson) {
          const user = JSON.parse(userJson);
          if (user.display_name === nickname && user.password_hash === `simple_${password}`) {
            return {
              id: user.id,
              display_name: user.display_name,
              email: undefined,
              cellphone: undefined,
              created_at: user.created_at,
            };
          }
        }
      }
      return null;
    } catch (error) {
      console.error('加载离线用户信息失败:', error);
      return null;
    }
  }

  private handleDeepLink = async (event: { url: string }) => {
    console.log('=== Deep Link Received ===');
    console.log('Raw URL:', event.url);

    if (event.url && event.url.includes('oauth/callback')) {
      try {
        console.log('=== Processing OAuth Callback ===');
        console.log('Full URL:', event.url);

        // 先检查URL是否是我们期望的scheme
        const expectedScheme = 'com.limeagent.chatfluent://';
        if (!event.url.startsWith(expectedScheme)) {
          console.warn('URL scheme mismatch. Expected:', expectedScheme, 'Got:', event.url.substring(0, expectedScheme.length));
        }

        // 解析URL参数 - Supabase通常在URL fragment或query中返回token
        const url = new URL(event.url);
        console.log('URL components:');
        console.log('  protocol:', url.protocol);
        console.log('  host:', url.host);
        console.log('  pathname:', url.pathname);
        console.log('  search:', url.search);
        console.log('  hash:', url.hash);

        // 尝试从hash中获取参数（常见情况）
        let params = new URLSearchParams();
        let paramSource = 'none';

        if (url.hash && url.hash.length > 1) {
          // 移除#号并解析参数
          const hashParams = url.hash.substring(1);
          console.log('Hash params string:', hashParams);
          params = new URLSearchParams(hashParams);
          paramSource = 'hash';
        } else if (url.search && url.search.length > 1) {
          // 从query参数中获取
          console.log('Query params string:', url.search);
          params = new URLSearchParams(url.search);
          paramSource = 'query';
        } else {
          // 尝试从整个URL中提取（有时token可能在URL的不同位置）
          console.log('No hash or query params found, trying to parse from full URL');
          const urlString = event.url;

          // 查找access_token的位置
          const tokenIndex = urlString.indexOf('access_token=');
          if (tokenIndex !== -1) {
            const paramString = urlString.substring(tokenIndex);
            console.log('Found token params in URL:', paramString);
            params = new URLSearchParams(paramString);
            paramSource = 'url_fragment';
          }
        }

        console.log('=== URL Parameters ===');
        console.log('Parameter source:', paramSource);
        console.log('Parameters found:', params.toString());

        // 打印所有参数以便调试
        let paramCount = 0;
        for (const [key, value] of params.entries()) {
          console.log(`  ${key}: ${value.substring(0, 50)}${value.length > 50 ? '...' : ''}`);
          paramCount++;
        }
        console.log('Total parameters:', paramCount);

        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        const tokenType = params.get('token_type');
        const expiresIn = params.get('expires_in');
        const error = params.get('error');
        const errorDescription = params.get('error_description');

        console.log('=== Token Information ===');
        console.log('Access token found:', !!accessToken);
        console.log('Refresh token found:', !!refreshToken);
        console.log('Token type:', tokenType);
        console.log('Expires in:', expiresIn);
        console.log('Error:', error);
        console.log('Error description:', errorDescription);

        if (error) {
          console.error('OAuth error received:', error, errorDescription);
          throw new Error(`OAuth error: ${error} - ${errorDescription}`);
        }

        if (accessToken) {
          console.log('=== Setting Session ===');
          console.log('Access token length:', accessToken.length);
          console.log('Refresh token length:', refreshToken?.length || 0);

          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });

          if (sessionError) {
            console.error('Error setting session:', sessionError);
            throw new Error(`Session error: ${sessionError.message}`);
          } else {
            console.log('Session set successfully');
            console.log('Session user:', data.session?.user?.email);
            console.log('Session expires at:', data.session?.expires_at);
          }
        } else {
          console.error('=== No Access Token Found ===');
          console.error('This usually means:');
          console.error('1. OAuth callback URL is malformed');
          console.error('2. URL parameters are in unexpected format');
          console.error('3. OAuth flow was cancelled or failed');
          throw new Error('No access token found in OAuth callback');
        }
      } catch (error) {
        console.error('=== OAuth Callback Error ===');
        console.error('Error:', error);
        throw error;
      }
    } else {
      console.log('Deep link does not contain oauth/callback, ignoring');
    }
  };

  async signInWithGoogle(): Promise<AuthUser> {
    try {
      console.log('Starting Google sign in with Supabase...');

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'com.limeagent.chatfluent://oauth/callback',
          skipBrowserRedirect: true,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        console.error('Supabase Google sign in error:', error);
        throw new Error(`Google登录失败: ${error.message}`);
      }

      if (data?.url) {
        console.log('OAuth URL generated:', data.url);
        console.log('Expected redirect URL should be: com.limeagent.chatfluent://oauth/callback');
        console.log('Please make sure this URL is configured in Supabase console under Authentication > URL Configuration > Redirect URLs');

        // 在React Native中，我们需要手动打开URL
        await Linking.openURL(data.url);

        // 等待认证完成
        const session = await this.waitForSession(60000); // 增加超时时间到60秒
        if (!session?.user) {
          throw new Error('登录完成但未获取到用户信息');
        }

        const authUser: AuthUser = {
          id: session.user.id,
          display_name: session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || '用户',
          email: session.user.email,
          created_at: session.user.created_at,
        };

        console.log('Google sign in successful:', authUser);
        return authUser;
      } else {
        throw new Error('未获取到OAuth认证URL');
      }
    } catch (error: any) {
      console.error('Google sign in error:', error);
      throw error;
    }
  }

  private async waitForSession(timeout = 30000): Promise<Session | null> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('登录超时，请重试'));
      }, timeout);

      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (event, session) => {
          if (event === 'SIGNED_IN' && session) {
            clearTimeout(timer);
            subscription.unsubscribe();
            resolve(session);
          } else if (event === 'SIGNED_OUT') {
            clearTimeout(timer);
            subscription.unsubscribe();
            resolve(null);
          }
        }
      );
    });
  }

  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        return null;
      }

      // 提取显示用的手机号（去掉国际区号前缀）
      const displayPhone = session.user.phone?.replace(/^\+\d{1,4}/, '') || '';

      return {
        id: session.user.id,
        display_name: session.user.user_metadata?.full_name ||
                     session.user.email?.split('@')[0] ||
                     displayPhone || '用户',
        email: session.user.email,
        cellphone: session.user.phone,
        created_at: session.user.created_at,
      };
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  // 手机号注册 (测试模式)
  async signUpWithCellphone(cellphone: string, password: string): Promise<void> {
    try {
      console.log('Signing up with cellphone (test mode):', cellphone);

      // 由于Supabase的手机号注册需要SMS验证，这里先使用模拟注册
      // 实际项目中应该调用: supabase.auth.signUp({ phone, password })

      console.log('Test cellphone sign up successful - user should login with existing test accounts');

      // 提示用户使用测试邮箱账号
      throw new Error('手机注册功能正在开发中，请使用测试邮箱账号登录: test1@chatfluent.dev - test9@chatfluent.dev，密码：123456');
    } catch (error: any) {
      console.error('Cellphone sign up error:', error);
      throw error;
    }
  }

  // 手机号密码登录 (暂时禁用)
  async signInWithCellphone(cellphone: string, password: string): Promise<AuthUser> {
    try {
      console.log('Attempting cellphone sign in:', cellphone);

      // 手机号登录暂时不可用，建议使用邮箱登录
      throw new Error('手机号登录功能正在开发中，请使用邮箱登录。测试账号: test1@chatfluent.dev - test9@chatfluent.dev，密码：123456');
    } catch (error: any) {
      console.error('Cellphone sign in error:', error);
      throw error;
    }
  }

  // 绑定手机号到现有账户
  async bindCellphone(cellphone: string, password: string): Promise<void> {
    try {
      console.log('Binding cellphone to account:', cellphone);

      const formattedPhone = cellphone.startsWith('+') ? cellphone : `+86${cellphone}`;

      const { error } = await supabase.auth.updateUser({
        phone: formattedPhone,
      });

      if (error) {
        console.error('Bind cellphone error:', error);
        throw new Error(`手机绑定失败: ${error.message}`);
      }

      console.log('Cellphone bind successful');
    } catch (error: any) {
      console.error('Bind cellphone error:', error);
      throw error;
    }
  }

  // 邮箱注册
  async signUpWithEmail(email: string, password: string): Promise<void> {
    try {
      console.log('Signing up with email:', email);

      const { data, error } = await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
          emailRedirectTo: undefined, // 对于测试账号，不发送验证邮件
        },
      });

      if (error) {
        console.error('Email sign up error:', error);
        throw new Error(`邮箱注册失败: ${error.message}`);
      }

      console.log('Email sign up successful');

      // 对于测试域名邮箱，提示用户可以直接登录
      if (email.includes('@chatfluent.dev')) {
        console.log('Test account created, can login directly');
      } else {
        console.log('Please check your email for verification');
      }
    } catch (error: any) {
      console.error('Email sign up error:', error);
      throw error;
    }
  }

  // 邮箱登录
  async signInWithEmail(email: string, password: string): Promise<AuthUser> {
    try {
      console.log('Signing in with email:', email);

      // 对于测试邮箱，使用自定义认证逻辑
      if (email.includes('@chatfluent.dev')) {
        return this.signInWithTestEmail(email, password);
      }

      // 对于真实邮箱，使用 Supabase Auth
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      if (error) {
        console.error('Email sign in error:', error);
        throw new Error(`邮箱登录失败: ${error.message}`);
      }

      if (!data.session?.user) {
        throw new Error('登录成功但未获取到用户信息');
      }

      const authUser: AuthUser = {
        id: data.session.user.id,
        display_name: data.session.user.user_metadata?.full_name ||
                     data.session.user.email?.split('@')[0] || '邮箱用户',
        email: data.session.user.email,
        cellphone: data.session.user.phone,
        created_at: data.session.user.created_at,
      };

      console.log('Email sign in successful:', authUser);
      return authUser;
    } catch (error: any) {
      console.error('Email sign in error:', error);
      throw error;
    }
  }

  // 昵称+密码注册
  async signUpWithNickname(nickname: string, password: string): Promise<AuthUser> {
    try {
      console.log('Signing up with nickname:', nickname);

      // 验证昵称格式
      if (!nickname || nickname.length < 2 || nickname.length > 20) {
        throw new Error('昵称长度应为2-20个字符');
      }

      if (password.length < 6) {
        throw new Error('密码长度至少6位');
      }

      // 检查昵称是否已存在 - 带网络错误处理
      let existingUsers = null;
      try {
        const { data, error: checkError } = await supabase
          .from('users')
          .select('id')
          .eq('display_name', nickname)
          .is('deleted_at', null);

        if (checkError) {
          console.error('Check nickname error:', checkError);
          console.warn('网络连接问题，使用本地昵称检查逻辑');
          // 网络失败时使用本地检查逻辑
          existingUsers = this.checkNicknameOffline(nickname);
        } else {
          existingUsers = data;
        }
      } catch (networkError) {
        console.warn('Supabase连接失败，使用离线昵称检查:', networkError);
        existingUsers = this.checkNicknameOffline(nickname);
      }

      if (existingUsers && existingUsers.length > 0) {
        throw new Error('昵称已被使用，请选择其他昵称');
      }

      // 创建新用户 - 带网络错误处理
      const userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const now = new Date().toISOString();

      try {
        const { data: newUser, error: userError } = await supabase
          .from('users')
          .insert({
            id: userId,
            display_name: nickname,
            oauth_provider: 'nickname',
            oauth_id: `nickname_${userId}`,
            created_at: now,
            metadata: { account_type: 'nickname', verified: false },
          })
          .select()
          .single();

        if (userError) {
          console.error('Create user error:', userError);
          throw new Error('创建用户失败');
        }

        // 创建认证方法记录
        const { error: authError } = await supabase
          .from('user_auth_methods')
          .insert({
            user_id: userId,
            provider: 'nickname',
            provider_user_id: nickname,
            password_hash: `simple_${password}`, // 简单存储，生产环境应该加密
            created_at: now,
          });

        if (authError) {
          console.error('Create auth method error:', authError);
          // 清理用户记录
          await supabase.from('users').delete().eq('id', userId);
          throw new Error('创建认证信息失败');
        }
      } catch (networkError) {
        console.warn('Supabase网络连接失败，创建离线用户账号:', networkError);
        // 网络失败时创建本地用户记录
        this.saveUserOffline(userId, nickname, password, now);
      }

      const authUser: AuthUser = {
        id: userId,
        display_name: nickname,
        email: undefined,
        cellphone: undefined,
        created_at: now,
      };

      // 注册时不需要创建Supabase session，登录时再创建匿名session

      console.log('Nickname sign up successful:', authUser);
      return authUser;
    } catch (error: any) {
      console.error('Nickname sign up error:', error);
      throw error;
    }
  }

  // 昵称+密码登录
  async signInWithNickname(nickname: string, password: string): Promise<AuthUser> {
    try {
      console.log('Signing in with nickname:', nickname);

      // 首先尝试从离线存储加载用户
      const offlineUser = await this.loadUserOffline(nickname, password);
      if (offlineUser) {
        console.log('使用离线用户信息登录:', offlineUser.display_name);
        // 为离线用户也尝试创建Supabase匿名session
        try {
          const { data: anonData } = await supabase.auth.signInAnonymously({
            options: {
              data: {
                display_name: offlineUser.display_name,
                is_nickname_user: true,
                original_user_id: offlineUser.id,
                is_offline_user: true,
              }
            }
          });
          if (anonData?.session?.access_token) {
            console.log('✅ 离线用户成功创建Supabase会话');
          }
        } catch (error) {
          console.warn('离线用户创建Supabase会话失败，继续使用离线模式');
        }
        return offlineUser;
      }

      // 尝试在线查询
      let authMethods = null;
      let users = null;
      
      try {
        // 查询认证方法
        const { data: authData, error } = await supabase
          .from('user_auth_methods')
          .select('*')
          .eq('provider', 'nickname')
          .eq('provider_user_id', nickname);

        if (error) {
          console.error('Database query error:', error);
          throw new Error('登录查询失败');
        }

        authMethods = authData;

        if (!authMethods || authMethods.length === 0) {
          throw new Error('用户不存在，请检查昵称或先注册');
        }

        const authMethod = authMethods[0];

        // 验证密码 (简单验证，生产环境应该使用加密)
        if (authMethod.password_hash !== `simple_${password}`) {
          throw new Error('密码错误，请重试');
        }

        // 查询用户信息
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('id', authMethod.user_id)
          .is('deleted_at', null);

        if (userError || !userData || userData.length === 0) {
          throw new Error('用户信息查询失败');
        }

        users = userData;
      } catch (networkError) {
        console.warn('Supabase连接失败，但找不到离线用户信息:', networkError);
        throw new Error('网络连接失败且未找到离线用户信息，请检查网络连接后重试');
      }

      const user = users[0];

      const authUser: AuthUser = {
        id: user.id,
        display_name: user.display_name,
        email: undefined,
        cellphone: undefined,
        created_at: user.created_at,
      };

      // 为昵称登录用户创建Supabase匿名session以生成JWT token
      try {
        console.log('Creating Supabase anonymous session for nickname user...');
        
        // 使用匿名认证为昵称用户创建session
        const { data: anonData, error: anonError } = await supabase.auth.signInAnonymously({
          options: {
            data: {
              display_name: user.display_name,
              is_nickname_user: true,
              original_user_id: user.id,
            }
          }
        });
        
        if (anonError) {
          console.warn('❌ Supabase anonymous session creation failed:', anonError.message);
          console.warn('Full error object:', anonError);
        } else if (anonData?.user && anonData?.session) {
          console.log('✅ Supabase anonymous session created successfully');
          console.log('Anonymous user ID:', anonData.user.id);
          console.log('Session token exists:', !!anonData.session.access_token);
          console.log('Session expires at:', anonData.session.expires_at);
          console.log('Token preview:', anonData.session.access_token?.substring(0, 20) + '...');
        } else {
          console.warn('⚠️ Supabase anonymous session created but no user/session data returned');
          console.log('Returned data:', anonData);
        }
        
      } catch (error) {
        console.warn('Failed to create Supabase anonymous session for nickname user:', error);
      }

      console.log('Nickname sign in successful:', authUser);
      return authUser;
    } catch (error: any) {
      console.error('Nickname sign in error:', error);
      throw error;
    }
  }

  // 测试邮箱登录（查询自定义数据库）
  private async signInWithTestEmail(email: string, password: string): Promise<AuthUser> {
    try {
      console.log('Testing custom email authentication for:', email);

      // 查询 user_auth_methods 表
      const { data: authMethods, error } = await supabase
        .from('user_auth_methods')
        .select('*')
        .eq('provider', 'email')
        .eq('provider_email', email);

      if (error) {
        console.error('Database query error:', error);
        throw new Error('数据库查询失败');
      }

      if (!authMethods || authMethods.length === 0) {
        throw new Error(`测试邮箱 ${email} 不存在。可用邮箱: test1@chatfluent.dev - test9@chatfluent.dev`);
      }

      const authMethod = authMethods[0];

      // 简单密码验证 (生产环境应该使用加密)
      if (password !== '123456') {
        throw new Error('密码错误，测试账号密码为: 123456');
      }

      // 查询用户信息
      const { data: users, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authMethod.user_id);

      if (userError || !users || users.length === 0) {
        throw new Error('用户信息查询失败');
      }

      const user = users[0];

      const authUser: AuthUser = {
        id: user.id,
        display_name: user.display_name || email.split('@')[0],
        email: email,
        cellphone: undefined,
        created_at: user.created_at,
      };

      console.log('Test email sign in successful:', authUser);
      return authUser;
    } catch (error: any) {
      console.error('Test email sign in error:', error);
      throw error;
    }
  }

  // 重置密码
  async resetPassword(email: string): Promise<void> {
    try {
      console.log('Resetting password for email:', email);

      const { error } = await supabase.auth.resetPasswordForEmail(email);

      if (error) {
        console.error('Reset password error:', error);
        throw new Error(`密码重置失败: ${error.message}`);
      }

      console.log('Password reset email sent successfully');
    } catch (error: any) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Logout error:', error);
        throw new Error(`登出失败: ${error.message}`);
      }
      console.log('Logout successful');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  async isLoggedIn(): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return !!session;
    } catch (error) {
      console.error('Check login status error:', error);
      return false;
    }
  }

  // 监听认证状态变化
  onAuthStateChange(callback: (user: AuthUser | null) => void) {
    return supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        // 提取显示用的手机号（去掉国际区号前缀）
        const displayPhone = session.user.phone?.replace(/^\+\d{1,4}/, '') || '';

        const authUser: AuthUser = {
          id: session.user.id,
          display_name: session.user.user_metadata?.full_name ||
                       session.user.email?.split('@')[0] ||
                       displayPhone || '用户',
          email: session.user.email,
          cellphone: session.user.phone,
          created_at: session.user.created_at,
        };
        callback(authUser);
      } else {
        callback(null);
      }
    });
  }
}

export default AuthService.getInstance();
